import * as z from 'zod/mini'

export const CheckboxEnum = z.enum(['on', 'off', 'has_data', 'none'])

export const CompanyConfig = z.object({
  tempEmpCalcBase: z.string(),
  tempEmpCalcMethod: z.string(),
  genderStatsAll: z.string(),
  genderStatsRegular: z.string(),
  genderStatsNonRegular: z.string()
})

/** @typedef {z.infer<typeof CompanyConfig>} CompanyConfig */

export const GroupCompany = z.object({
  searchKey: z.string(),
  corporationNumber: z.string(),
  subCorporationNumber: z.string(),
  compName: z.string(),
  compType: z.string(),
  isChrofyEnabled: z.boolean(),
  employeeCountInfo: z.object({
    registrationStatus: z.boolean(),
    aggregationTarget: CheckboxEnum,
    includeInSubmittingCompanyStatus: CheckboxEnum,
    regularEmployee: z.optional(z.number()),
    temporaryEmployee: z.optional(z.number())
  }),
  genderStatisticsInfo: z.object({
    registrationStatus: z.boolean(),
    femaleManagerRatio: CheckboxEnum,
    parentalLeaveRate: CheckboxEnum,
    wageGap: CheckboxEnum
  })
})

/** @typedef {z.infer<typeof GroupCompany>} GroupCompany */

const SecuritiesConfig = z.object({
  corporation_number: z.string(),
  sub_corporation_number: z.string(),
  search_key: z.string(),
  comp_name: z.string(),
  comp_type: z.string(),
  is_chrofy_enabled: z.boolean(),
  employee_count_info: z.object({
    registration_status: z.boolean(),
    aggregation_target: CheckboxEnum,
    include_in_submitting_company_status: z._default(z.optional(CheckboxEnum), 'none'),
    regular_employee: z.optional(z.number()),
    temporary_employee: z.optional(z.number())
  }),
  gender_statistics_info: z.object({
    registration_status: z.boolean(),
    female_manager_ratio: CheckboxEnum,
    parental_leave_rate: CheckboxEnum,
    wage_gap: CheckboxEnum
  })
})
/** @typedef {z.infer<typeof SecuritiesConfig>} SecuritiesConfig */

/** APIからのレスポンス */
export const SecuritiesReportResponse = z.object({
  corporation_number: z.string(),
  fiscal_year: z.string(),
  company_config: z.object({
    temp_emp_calc_base: z.string(),
    temp_emp_calc_method: z.string(),
    gender_stats_all: z.string(),
    gender_stats_regular: z.string(),
    gender_stats_non_regular: z.string()
  }),
  securities_config: z.array(SecuritiesConfig)
})

/**
 * @function assertSecuritiesReportResponseSchema
 * @param {object} data
 * @returns {z.infer<typeof SecuritiesReportResponse>}
 */
export const assertSecuritiesReportResponseSchema = (data) => SecuritiesReportResponse.parse(data)

export const PageStateValueSchema = z.object({
  companyConfig: CompanyConfig,
  groupCompany: z.array(GroupCompany)
})

/** @typedef {z.infer<typeof PageStateValueSchema>} PageStateValueSchema */

export const PageState = z.record(z.string(), PageStateValueSchema)

/** 画面状態 */
export const SecuritiesReportStateSchema = z.object({
  mainStore: z.record(z.string(), z.unknown()),
  yearList: z.array(z.object()),
  selectedFiscalYear: z.string(),
  pageState: PageState,

  master: z.object({
    Classification: z.string()
  }),
  errors: z.object({
    server: z.array(z.string()),
    client: z.record(z.string(), z.array(z.string()))
  })
})

/**
 * @function assetSecuritiesReportStateSchema
 * @param {object} value
 * @returns {z.infer<typeof SecuritiesReportStateSchema>}
 */
export const assetSecuritiesReportStateSchema = (value) => SecuritiesReportStateSchema.parse(value)

export const SecuritiesReportPostRequestSchema = z.object({
  corporation_number: z.string(),
  user_name: z.string(),
  fiscal_period: z.string(),
  group_company: z.array(
    z.object({
      search_key: z.string(),
      comp_number: z.string(),
      aggregation_target: CheckboxEnum,
      include_in_submitting_company_status: CheckboxEnum,
      female_manager_ratio: CheckboxEnum,
      parental_leave_rate: CheckboxEnum,
      wage_gap: CheckboxEnum
    })
  ),
  company_config: z.object({
    key: z.string(),
    comp_number: z.string(),
    temp_emp_calc_base: z.string(),
    temp_emp_calc_method: z.string(),
    gender_stats_all: z.string(),
    gender_stats_regular: z.string(),
    gender_stats_non_regular: z.string()
  })
})

export const assertSecuritiesReportPostRequest = (value) => SecuritiesReportPostRequestSchema.parse(value)

/** @param {import('yup').InferType<typeof SecuritiesReportResponse>} securitiesConfig */
export const convertRCompanyConfigToS = (securitiesConfig) => {
  const { temp_emp_calc_base, temp_emp_calc_method, gender_stats_all, gender_stats_regular, gender_stats_non_regular } =
    securitiesConfig.company_config

  return CompanyConfig.parse({
    tempEmpCalcBase: temp_emp_calc_base,
    tempEmpCalcMethod: temp_emp_calc_method,
    genderStatsAll: gender_stats_all,
    genderStatsRegular: gender_stats_regular,
    genderStatsNonRegular: gender_stats_non_regular
  })
}

/**
 * @function convertRGroupCompanyToS レスポンスから状態に変換
 * @param {z.infer<typeof SecuritiesReportResponse>} securitiesConfig
 */
export const convertRGroupCompanyToS = (securitiesConfig) => {
  const _securitiesConfig = securitiesConfig.securities_config
  return _securitiesConfig.map((config) => {
    const {
      corporation_number,
      sub_corporation_number,
      search_key,
      comp_name,
      comp_type,
      is_chrofy_enabled,
      employee_count_info,
      gender_statistics_info
    } = config
    return GroupCompany.parse({
      searchKey: search_key,
      corporationNumber: corporation_number,
      subCorporationNumber: sub_corporation_number,
      compName: comp_name,
      compType: comp_type,
      isChrofyEnabled: is_chrofy_enabled,
      employeeCountInfo: {
        registrationStatus: employee_count_info.registration_status,
        aggregationTarget: employee_count_info.aggregation_target,
        // API が未返却の場合は none（SecuritiesConfig の default と揃える）
        includeInSubmittingCompanyStatus: employee_count_info.include_in_submitting_company_status ?? 'none',
        ...(employee_count_info.regular_employee !== undefined
          ? { regularEmployee: employee_count_info.regular_employee }
          : {}),
        ...(employee_count_info.temporary_employee !== undefined
          ? { temporaryEmployee: employee_count_info.temporary_employee }
          : {})
      },
      genderStatisticsInfo: {
        registrationStatus: gender_statistics_info.registration_status,
        femaleManagerRatio: gender_statistics_info.female_manager_ratio,
        parentalLeaveRate: gender_statistics_info.parental_leave_rate,
        wageGap: gender_statistics_info.wage_gap
      }
    })
  })
}
